import { useMemo, useState } from 'react';
import type { Lang, Reservation, ReservationStatus } from '../data/types';
import { min } from '../data/mock';
import { useDashboard } from '../data/context';
import { t } from '../i18n/t';
import { Icon } from '../components/Icon';
import { Avatar } from '../components/Avatar';
import { Pill } from '../components/Pill';
import { Tags } from '../components/Tags';

type Filter = 'all' | ReservationStatus;

interface BookingsViewProps {
  lang: Lang;
  onOpen: (r: Reservation) => void;
}

const GRID_STYLE = { gridTemplateColumns: '2fr 54px 76px 0.7fr 0.9fr 116px' };

export function BookingsView({ lang, onOpen }: BookingsViewProps) {
  const { reservations: allReservations } = useDashboard();
  const [filter, setFilter] = useState<Filter>('all');
  const [q, setQ] = useState('');

  const counts = useMemo(
    () => ({
      all: allReservations.length,
      new: allReservations.filter((r) => r.status === 'new').length,
      conf: allReservations.filter((r) => r.status === 'conf').length,
      seated: allReservations.filter((r) => r.status === 'seated').length,
      cancel: allReservations.filter((r) => r.status === 'cancel').length,
    }),
    [allReservations],
  );

  const filters: { id: Filter; labelKey: string; n: number }[] = [
    { id: 'all', labelKey: 'all', n: counts.all },
    { id: 'new', labelKey: 'st_new', n: counts.new },
    { id: 'conf', labelKey: 'st_conf', n: counts.conf },
    { id: 'seated', labelKey: 'st_seated', n: counts.seated },
    { id: 'cancel', labelKey: 'st_cancel', n: counts.cancel },
  ];

  const rows = useMemo(() => {
    let list = [...allReservations].sort((a, b) => {
      // dinner first, then lunch; within service, by time
      const order: Record<string, number> = { cena: 1, pranzo: 0 };
      if (order[a.service] !== order[b.service]) return order[b.service] - order[a.service];
      return min(a.start) - min(b.start);
    });
    if (filter !== 'all') list = list.filter((r) => r.status === filter);
    const k = q.trim().toLowerCase();
    if (k) {
      list = list.filter(
        (r) => r.name.toLowerCase().includes(k) || (r.phone || '').includes(k),
      );
    }
    return list;
  }, [filter, q, allReservations]);

  return (
    <div className="view">
      <div className="view-pad">
        <div className="filterbar">
          {filters.map((f) => (
            <button
              key={f.id}
              type="button"
              className={'fchip' + (filter === f.id ? ' on' : '')}
              onClick={() => setFilter(f.id)}
            >
              {t(f.labelKey, lang)}
              <span className="n">{f.n}</span>
            </button>
          ))}
          <div style={{ flex: 1 }} />
          <div className="search" style={{ minWidth: 220 }}>
            <Icon name="search" size={15} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('search', lang)}
              aria-label={t('search', lang)}
            />
          </div>
        </div>

        <div className="tablewrap">
          <div className="thead" style={GRID_STYLE} role="row">
            <span>{t('name', lang)}</span>
            <span>{t('party', lang)}</span>
            <span>{t('time', lang)}</span>
            <span>{t('table', lang)}</span>
            <span>{t('source', lang)}</span>
            <span>{t('status', lang)}</span>
          </div>
          {rows.map((r) => (
            <div
              key={r.id}
              className="trow"
              style={GRID_STYLE}
              onClick={() => onOpen(r)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onOpen(r);
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={`${r.name} — ${r.covers} ${t('covers', lang)} — ${r.start}`}
            >
              <span className="cell-name">
                <Avatar name={r.name} size="sm" />
                <span className="nm">{r.name}</span>
                <Tags tags={r.tags} lang={lang} max={1} />
              </span>
              <span className="cell-num">{r.covers}</span>
              <span className="cell-num tnum">{r.start}</span>
              <span className="cell-mute">{r.table}</span>
              <span className="src">{t(`src_${r.source}`, lang)}</span>
              <span>
                <Pill status={r.status} lang={lang} />
              </span>
            </div>
          ))}
          {rows.length === 0 && (
            <div style={{ padding: 40, textAlign: 'center' }} className="hint">
              {t('noBookings', lang)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
